import React, {useCallback, useState} from "react";
import styled from "styled-components";
import {useDropzone} from 'react-dropzone';

import {Spinner} from "@chakra-ui/react";
import {Plus} from "lucide-react";
import useFeedback from "../utils/use_feedback";
import {useCreateImage, useCreateVideo, useUploadToken} from "../api/create_image";
import {themecolors} from "../utils/themecolors";
import {extension} from "../utils/files";
import {useStoreProfile} from "../store";
import ErrorMessage from "./error_message";

const PreviewImg = styled.img`
    border-radius: 12px;
`;

const AcceptedTypes = styled.p``;

const PreviewVideo = styled.video`
    max-height: 200px;
    max-width: 100%;
`;
const PreviewContainer = styled.div``;
const SpinnerContainer = styled.div`
position: absolute;
width: 100%;
`;

const permittedFiletypes = ['jpg', 'jpeg', 'gif', 'png', 'mp4', 'webp'];

const ImageField = styled(({className, id, label, value, onChange}) => {

    const {theme} = useStoreProfile();
    const {busy, error, feedbackCh} = useFeedback();
    const upload = useCreateImage(feedbackCh);
    const uploadVideo = useCreateVideo(feedbackCh);
    const uploadToken = useUploadToken(feedbackCh);

    const [rejection, setRejection] = useState('');
    const [preview, setPreview] = useState('');
    const [fileExtensionVal, setFileExtensionVal] = useState(false);

    const [success, setSuccess] = useState(false);

    const handleImage = async (files) =>{

        try {
            const fileExtension = extension(files[0].name).toLowerCase();
            setFileExtensionVal(fileExtension);
            const fileSize = files[0].size / 1024 / 1024;
            if (!permittedFiletypes.includes(fileExtension)) {
                setRejection('That filetype is not accepted, please try again.');
                return;
            }
            if (fileExtension !== 'mp4' && fileExtension !== 'gif' && fileSize > 12) {
                setRejection('Maximum image size is 12MB, please try again.');
                return;
            } else if ((fileExtension === 'mp4' || fileExtension === 'gif') && fileSize > 100) {
                setRejection('Maximum video size is 100MB, please try again.');
                return;
            }

            const previewURL = URL.createObjectURL(files[0]);
            setPreview(previewURL);

            if (fileExtension === 'mp4') {
                const id = await uploadVideo(files[0]);
                onChange(`https://cdn.videy.co/${id}.mp4`);
                setSuccess(true);
            } else {
                const token = await uploadToken('postMedia');
                const id = await upload(files[0], token);
                onChange(`https://media.scored.co/post/${  id}`);
                setSuccess(true);
            }
        } catch (err) {
            // error is displayed
            setPreview(false);
        }

    }

    const onDrop = useCallback(async (files) => {
        setRejection('');
        await handleImage(files);
    }, [setPreview, upload, onChange]);
    const {getRootProps, getInputProps, isDragActive} = useDropzone({onDrop});

    return (
        <div className={className}>
            {preview &&
                <PreviewContainer>
                    {busy &&
                        <SpinnerContainer>
                            <Spinner
                                thickness='4px'
                                speed='0.65s'
                                emptyColor={theme === "dark" ? themecolors.dark_background_5 : themecolors.light_low_impact_text}
                                color={theme === "dark" ? themecolors.dark_background_2 : themecolors.pure_white}
                                size='lg'
                            />
                        </SpinnerContainer>
                    }
                    {(fileExtensionVal && fileExtensionVal === 'mp4') ? (
                    <PreviewVideo controls={false} autoplay={false} src={preview} />
                    ) : (
                    <PreviewImg src={preview} />
                    )}
                </PreviewContainer>
            }
            {!preview && <figure {...getRootProps()}>
                <input {...getInputProps()} />

                {!busy &&
                    <Plus size={32} color={theme === "dark" ? themecolors.dark_background_4 : "rgba(0,0,0,.1)"} />
                }

            </figure>
            }

            {error?.toString()}
            {rejection &&
            <p>{rejection}</p>
            }

            <ErrorMessage error={error}/>
        </div>
    )
})`
  display: flex;
  flex-direction: column;
  margin-top: 8px;
  margin-bottom: 24px;
  ${AcceptedTypes} {
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_medium_impact_text : themecolors.light_medium_impact_text};
  }
  figure {
    width: 180px;
    height: 180px;
    margin: 0;
    border: 2px dashed ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_background_5 : themecolors.light_border};
    border-radius: 8px;
    display: flex;
    justify-content: center;
    margin-bottom: 12px;
    align-items: center;
  }
  figcaption {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    text-align: center;
  }
  figcaption p {
    font-size: 17px;
    font-weight: 600;
  }
  ${AcceptedTypes} {
    font-size: 15px;
    font-weight: 400;
  }
  figcaption .small {
    font-size: 80%;
  }
  label {
    font-size: 16px;
    font-weight: 500;
  }
  ${PreviewContainer} {
    display: flex;
    align-items: center;
    justify-content: center;
  }
  ${SpinnerContainer} {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

export default ImageField;
